// This is the browser-safe public seam for Candidate Visualization. The
// operation grammar remains deliberately allow-listed: full scene replacement
// is never a valid edit. It is distinct from immutable Production Specification
// and does not expose any Unreal packages or mutation capability.
import {
  createDefaultMegastructureVisualization,
  stackedMegastructureVisualizationSchema,
} from "./megastructure-visualization.js";

export {
  applyMegastructureVisualizationOperations,
  megastructureVisualizationAdditionSchema,
  megastructureVisualizationOperationPathSchema,
  megastructureVisualizationOperationSchema,
  megastructureVisualizationReplySchema,
  stackedMegastructureVisualizationSchema,
} from "./megastructure-visualization.js";

// The immutable default is defined once with the schema and reused here for
// browser compatibility normalization.
export const DEFAULT_MEGASTRUCTURE_VISUALIZATION = Object.freeze(createDefaultMegastructureVisualization());

const PARIS_EIFFEL_ORIGIN = { latitude: 48.85837, longitude: 2.294481 };
const PARIS_EIFFEL_PRESET = Object.freeze({
  widthM: 12_000,
  depthM: 10_000,
  eiffelTower: { widthM: 125, heightM: 330 },
  landmarks: [
    { id: "arc_de_triomphe", latitude: 48.873792, longitude: 2.295028, widthM: 45, depthM: 22, heightM: 50, color: "#d8d0bf" },
    { id: "louvre", latitude: 48.860611, longitude: 2.337644, widthM: 420, depthM: 160, heightM: 30, color: "#c8b99f" },
    { id: "notre_dame", latitude: 48.852968, longitude: 2.349902, widthM: 130, depthM: 48, heightM: 96, color: "#b7ad9a" },
    { id: "sacre_coeur", latitude: 48.886705, longitude: 2.343104, widthM: 85, depthM: 85, heightM: 83, color: "#ded8c9" },
    { id: "tour_montparnasse", latitude: 48.8421, longitude: 2.3219, widthM: 100, depthM: 60, heightM: 210, color: "#59636d" },
  ],
  seine: [
    [48.8468, 2.2300], [48.8508, 2.2630], [48.8564, 2.2910], [48.8612, 2.3150],
    [48.8594, 2.3370], [48.8530, 2.3500], [48.8465, 2.3650], [48.8395, 2.3830],
  ],
});

function parisLocalPosition(latitude, longitude) {
  const metresPerLatitudeDegree = 111_320;
  const metresPerLongitudeDegree = metresPerLatitudeDegree * Math.cos(PARIS_EIFFEL_ORIGIN.latitude * Math.PI / 180);
  return {
    xM: (longitude - PARIS_EIFFEL_ORIGIN.longitude) * metresPerLongitudeDegree,
    zM: (latitude - PARIS_EIFFEL_ORIGIN.latitude) * metresPerLatitudeDegree,
  };
}

function distanceToSegment(x, z, start, end) {
  const dx = end.xM - start.xM;
  const dz = end.zM - start.zM;
  const lengthSquared = dx * dx + dz * dz || 1;
  const t = Math.max(0, Math.min(1, ((x - start.xM) * dx + (z - start.zM) * dz) / lengthSquared));
  return Math.hypot(x - (start.xM + t * dx), z - (start.zM + t * dz));
}

function cityStructureOffset(addition, state) {
  const direction = {
    east: { x: 1, z: 0 }, west: { x: -1, z: 0 }, north: { x: 0, z: 1 }, south: { x: 0, z: -1 },
  }[addition.structureDirection];
  const eiffelFootprintRadiusM = PARIS_EIFFEL_PRESET.eiffelTower.widthM / Math.sqrt(2);
  const centreDistanceM = state.tiers.disk.diameterM / 2 + eiffelFootprintRadiusM + addition.clearanceM;
  return { x: direction.x * centreDistanceM, z: direction.z * centreDistanceM };
}

function buildParisContext(addition, state, structureTransforms) {
  const structureOffsetM = cityStructureOffset(addition, state);
  const riverPoints = PARIS_EIFFEL_PRESET.seine.map(([latitude, longitude]) => parisLocalPosition(latitude, longitude));
  const riverSegments = riverPoints.slice(1).map((end, index) => ({ start: riverPoints[index], end, widthM: 120 }));
  const buildings = [];
  for (let xM = -5_700, column = 0; xM <= 5_700; xM += 600, column += 1) {
    for (let zM = -4_750, row = 0; zM <= 4_750; zM += 500, row += 1) {
      const outsideParis = (xM / 6_000) ** 2 + (zM / 5_000) ** 2 > 1;
      const blockHalfDiagonalM = Math.hypot(420 / 2, 340 / 2);
      const insideStructure = structureTransforms.some((transform) => Math.hypot(xM - transform.xM, zM - transform.zM) < state.tiers.disk.diameterM / 2 + blockHalfDiagonalM + 40);
      const nearEiffel = Math.hypot(xM, zM) < 190;
      const inRiver = riverSegments.some((segment) => distanceToSegment(xM, zM, segment.start, segment.end) < segment.widthM / 2 + 35);
      const boulevardGap = Math.abs(xM) < 90 || Math.abs(zM) < 90;
      if (outsideParis || insideStructure || nearEiffel || inRiver || boulevardGap) continue;
      const hash = Math.abs(((column + 17) * 73_856_093) ^ ((row + 31) * 19_349_663));
      const heightM = 12 + hash % 26 + (hash % 97 === 0 ? 28 : 0);
      buildings.push({ id: `paris_block_${column}_${row}`, xM, zM, widthM: 420, depthM: 340, heightM });
    }
  }
  return {
    ...addition,
    widthM: PARIS_EIFFEL_PRESET.widthM,
    depthM: PARIS_EIFFEL_PRESET.depthM,
    structureOffsetM,
    eiffelTower: { ...PARIS_EIFFEL_PRESET.eiffelTower, xM: 0, zM: 0 },
    landmarks: PARIS_EIFFEL_PRESET.landmarks.map((landmark) => ({
      ...landmark,
      ...parisLocalPosition(landmark.latitude, landmark.longitude),
    })),
    riverSegments,
    buildings,
    scale: "1 world unit = 1 metre",
  };
}

export function cloneDefaultMegastructureVisualization() {
  return structuredClone(DEFAULT_MEGASTRUCTURE_VISUALIZATION);
}

function seededRandom(seed) {
  let value = seed >>> 0;
  return () => {
    value += 0x6d2b79f5;
    let mixed = value;
    mixed = Math.imul(mixed ^ mixed >>> 15, mixed | 1);
    mixed ^= mixed + Math.imul(mixed ^ mixed >>> 7, mixed | 61);
    return ((mixed ^ mixed >>> 14) >>> 0) / 4_294_967_296;
  };
}

/** Seeded jittered lattice with a mathematically bounded minimum separation.
 * The full candidate order is count-independent, so existing IDs stay fixed as count grows. */
export function buildMegastructureArrayTransforms(structureArray, anchor = { x: 0, z: 0 }, footprintDiameterM = 0) {
  const transforms = [{ id: "megastructure_000", index: 0, xM: anchor.x, zM: anchor.z, rotationYRad: 0 }];
  if (structureArray.instanceCount === 1) return transforms;
  const random = seededRandom(structureArray.seed);
  const separationM = footprintDiameterM + structureArray.minimumClearanceM;
  const cellSizeM = structureArray.spacingM;
  if (cellSizeM < separationM) throw new Error("sky-elevator spacing must exceed the disk footprint and clearance");
  const jitterAxisM = (cellSizeM - separationM) / 2 * structureArray.randomness;
  const jitterRadiusM = Math.SQRT2 * jitterAxisM;
  const distributionRadiusM = structureArray.spreadDiameterM / 2;
  const latticeLimit = Math.floor(Math.max(0, distributionRadiusM - jitterRadiusM) / cellSizeM);
  const candidates = [];
  for (let x = -latticeLimit; x <= latticeLimit; x += 1) {
    for (let z = -latticeLimit; z <= latticeLimit; z += 1) {
      if (x === 0 && z === 0) continue;
      const baseXM = x * cellSizeM;
      const baseZM = z * cellSizeM;
      if (Math.hypot(baseXM, baseZM) + jitterRadiusM > distributionRadiusM) continue;
      candidates.push({
        baseDistanceM: Math.hypot(baseXM, baseZM),
        orderKey: random(),
        xM: anchor.x + baseXM + (random() * 2 - 1) * jitterAxisM,
        zM: anchor.z + baseZM + (random() * 2 - 1) * jitterAxisM,
        rotationYRad: (random() - 0.5) * Math.PI * 2 * structureArray.randomness,
      });
    }
  }
  // Fill nearest rings first so replica 2 is one explicit spacing step from
  // the original; seed only chooses direction/order within an equal ring.
  candidates.sort((a, b) => a.baseDistanceM - b.baseDistanceM || a.orderKey - b.orderKey);
  if (candidates.length < structureArray.instanceCount - 1) throw new Error("whole-structure spread cannot fit the requested count and clearance");
  candidates.slice(0, structureArray.instanceCount - 1).forEach(({ baseDistanceM: _distance, orderKey: _order, ...candidate }, offset) => transforms.push({
    id: `megastructure_${String(offset + 1).padStart(3, "0")}`,
    index: offset + 1,
    ...candidate,
  }));
  return transforms;
}

/** Normalize and validate a mutable Candidate Visualization. This browser-safe module never authorizes Production Specification or Unreal mutation. */
export function validateMegastructureVisualization(scene) {
  if (!scene || typeof scene !== "object" || scene.schemaVersion !== DEFAULT_MEGASTRUCTURE_VISUALIZATION.schemaVersion || scene.scope !== "candidate_visualization") {
    throw new Error("unsupported megastructure visualization schema");
  }
  const copy = structuredClone(scene);
  copy.edit = {
    ...DEFAULT_MEGASTRUCTURE_VISUALIZATION.edit,
    ...copy.edit,
    blenderSource: {
      ...DEFAULT_MEGASTRUCTURE_VISUALIZATION.edit.blenderSource,
      ...copy.edit?.blenderSource,
    },
  };
  copy.structureArray = {
    ...DEFAULT_MEGASTRUCTURE_VISUALIZATION.structureArray,
    ...(copy.structureArray || {}),
  };
  copy.additions = Array.isArray(copy.additions) ? copy.additions : [];
  try {
    return stackedMegastructureVisualizationSchema.parse(copy);
  } catch (error) {
    // Keep the compatibility interface used by the existing visualizer while
    // the schema remains the single semantic authority.
    if (error instanceof Error && error.message.includes("spread fits at most")) {
      throw new Error("whole-structure spread cannot fit the requested count and clearance");
    }
    throw error;
  }
}

/** Renderer-neutral instance plan. Browser geometry is a projection, not an asset revision. */
export function buildMegastructureVisualizationPlan(scene) {
  const state = validateMegastructureVisualization(scene);
  const cityAddition = state.additions.find((addition) => addition.kind === "geographic_city_context") || null;
  const structureOffsetM = cityAddition ? cityStructureOffset(cityAddition, state) : { x: 0, z: 0 };
  const structureTransforms = buildMegastructureArrayTransforms(state.structureArray, structureOffsetM, state.tiers.disk.diameterM);
  const cityContext = cityAddition ? buildParisContext(cityAddition, state, structureTransforms) : null;
  const tierBasesM = Array.from({ length: state.tiers.count }, (_, index) => index * state.tiers.verticalPitchM);
  const localTopM = Math.max(
    state.tiers.disk.heightM,
    state.tiers.disk.heightM + state.tiers.perimeterWall.heightM,
    state.tiers.centralPillar.heightM,
  );
  const tierTransforms = (centreForBase) => structureTransforms.flatMap((structure) => tierBasesM.map((baseM, tierIndex) => ({
    structureInstanceId: structure.id,
    structureIndex: structure.index,
    tierIndex,
    xM: structure.xM,
    yM: centreForBase(baseM),
    zM: structure.zM,
    rotationYRad: structure.rotationYRad,
  })));
  // Stable order and angle convention: 0° is +X/east; positive turns toward +Z/north.
  const additions = [...state.additions].filter((addition) => addition.kind === "radial_tower_array").sort((a, b) => a.id.localeCompare(b.id)).map((addition) => {
    const tierIndices = addition.appliesTo === "every_tier"
      ? Array.from({ length: state.tiers.count }, (_, index) => index)
      : [0];
    const angleCount = Math.max(1, Math.ceil((360 - 1e-9) / addition.spacingDeg));
    const centreRadiusM = state.tiers.disk.diameterM / 2
      - state.tiers.perimeterWall.radialThicknessM / 2
      + addition.radialOffsetM;
    const transforms = [];
    // Typed additions remain on the canonical elevator unless their own
    // explicit scope opts into every sky-elevator root.
    const additionStructures = addition.replicationScope === "each_full_stack" ? structureTransforms : structureTransforms.slice(0, 1);
    for (const structure of additionStructures) {
      const cosRoot = Math.cos(structure.rotationYRad);
      const sinRoot = Math.sin(structure.rotationYRad);
      for (const tierIndex of tierIndices) {
        const centreYM = tierBasesM[tierIndex]
          + state.tiers.disk.heightM
          + state.tiers.perimeterWall.heightM
          + addition.baseOffsetM
          + addition.heightM / 2;
        for (let angleIndex = 0; angleIndex < angleCount; angleIndex += 1) {
          const angleDeg = addition.angularOffsetDeg + angleIndex * addition.spacingDeg;
          const angleRad = angleDeg * Math.PI / 180;
          const localXM = Math.cos(angleRad) * centreRadiusM;
          const localZM = Math.sin(angleRad) * centreRadiusM;
          transforms.push({
            structureInstanceId: structure.id,
            structureIndex: structure.index,
            tierIndex,
            angleDeg,
            xM: structure.xM + cosRoot * localXM + sinRoot * localZM,
            yM: centreYM,
            zM: structure.zM - sinRoot * localXM + cosRoot * localZM,
            rotationYRad: structure.rotationYRad - angleRad,
          });
        }
      }
    }
    return { ...addition, centreRadiusM, transforms };
  });
  const additionTopM = additions.flatMap((addition) => addition.transforms.map((transform) => transform.yM + addition.heightM / 2));
  const footprintRadiusM = state.tiers.disk.diameterM / 2;
  const bounds = structureTransforms.reduce((result, transform) => ({
    minX: Math.min(result.minX, transform.xM - footprintRadiusM),
    maxX: Math.max(result.maxX, transform.xM + footprintRadiusM),
    minZ: Math.min(result.minZ, transform.zM - footprintRadiusM),
    maxZ: Math.max(result.maxZ, transform.zM + footprintRadiusM),
  }), { minX: Infinity, maxX: -Infinity, minZ: Infinity, maxZ: -Infinity });
  if (cityContext) {
    bounds.minX = Math.min(bounds.minX, -cityContext.widthM / 2);
    bounds.maxX = Math.max(bounds.maxX, cityContext.widthM / 2);
    bounds.minZ = Math.min(bounds.minZ, -cityContext.depthM / 2);
    bounds.maxZ = Math.max(bounds.maxZ, cityContext.depthM / 2);
  }
  const expandGround = state.structureArray.autoExpandLandscape && structureTransforms.length > 1;
  const requiredWidthM = bounds.maxX - bounds.minX + state.structureArray.groundPaddingM * 2;
  const requiredDepthM = bounds.maxZ - bounds.minZ + state.structureArray.groundPaddingM * 2;
  const groundWidthM = expandGround ? Math.max(state.landscape.widthM, requiredWidthM) : state.landscape.widthM;
  const groundDepthM = expandGround ? Math.max(state.landscape.depthM, requiredDepthM) : state.landscape.depthM;
  const diskCentresYM = tierBasesM.map((base) => base + state.tiers.disk.heightM / 2);
  const wallCentresYM = tierBasesM.map((base) => base + state.tiers.disk.heightM + state.tiers.perimeterWall.heightM / 2);
  const pillarCentresYM = tierBasesM.map((base) => base + state.tiers.centralPillar.heightM / 2);
  return {
    state,
    ground: {
      widthM: groundWidthM,
      depthM: groundDepthM,
      thicknessM: state.landscape.thicknessM,
      centreYM: -state.landscape.thicknessM / 2,
      centreXM: (bounds.minX + bounds.maxX) / 2,
      centreZM: (bounds.minZ + bounds.maxZ) / 2,
      visible: !cityContext || structureTransforms.length > 1,
      autoExpanded: expandGround && (groundWidthM > state.landscape.widthM || groundDepthM > state.landscape.depthM),
    },
    disk: {
      radiusM: state.tiers.disk.diameterM / 2,
      heightM: state.tiers.disk.heightM,
      centresYM: diskCentresYM,
      transforms: tierTransforms((base) => base + state.tiers.disk.heightM / 2),
    },
    wall: {
      outerRadiusM: state.tiers.disk.diameterM / 2,
      innerRadiusM: state.tiers.disk.diameterM / 2 - state.tiers.perimeterWall.radialThicknessM,
      heightM: state.tiers.perimeterWall.heightM,
      centresYM: wallCentresYM,
      transforms: tierTransforms((base) => base + state.tiers.disk.heightM + state.tiers.perimeterWall.heightM / 2),
    },
    pillar: {
      radiusM: state.tiers.centralPillar.diameterM / 2,
      heightM: state.tiers.centralPillar.heightM,
      centresYM: pillarCentresYM,
      transforms: tierTransforms((base) => base + state.tiers.centralPillar.heightM / 2),
    },
    additions,
    cityContext,
    structureOffsetM,
    structureTransforms,
    overallHeightM: Math.max(tierBasesM.at(-1) + localTopM, ...additionTopM),
    instanceCount: state.tiers.count * 3 * structureTransforms.length + 1
      + additions.reduce((count, addition) => count + addition.transforms.length, 0)
      + (cityContext ? cityContext.buildings.length + cityContext.landmarks.length + cityContext.riverSegments.length + 2 : 0),
  };
}
